import { trackEvent, AnalyticsEvents } from '../analytics/instance';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { API_URL, ApiError, api, apiFormData, getAuthHeaders, getErrorMessage } from '../utils/api';
import { showToast } from '../utils/toast';

export interface EssayNode {
  id: string;
  text: string;
  children: EssayNode[];
}

export interface EssayContent {
  done: EssayNode[];
  issues: EssayNode[];
  plans: EssayNode[];
  interesting: EssayNode[];
}

export type EssayStatus = 'draft' | 'submitted';

export interface EssaySummary {
  _id: string;
  user: string;
  year: number;
  month: number;
  status: EssayStatus;
  submittedAt?: string | null;
  // Эссе заведено импортом из смежной системы, а не самим человеком.
  importedAt?: string | null;
  // Пометка «вернули на доработку». Снимается следующей сдачей.
  returnedForRework?: ReworkNote | null;
  createdAt: string;
  updatedAt: string;
}

export interface Essay extends EssaySummary, EssayContent {}

export interface ReworkNote {
  at: string;
  // Подгружается с именем на чтении документа; в других ответах — просто id.
  by: string | { _id: string; name?: string };
  comment: string;
}

export interface EssayResponse {
  essay: Essay;
  canEdit: boolean;
  /**
   * Может ли смотрящий сдать эссе и вернуть его в черновик. Своё сданное не правит никто
   * (canEdit: false), а расчерновать его можно.
   */
  canSubmit: boolean;
  /** Может ли смотрящий вернуть эссе автору. */
  canReturn: boolean;
}

/** Одна строка сводки за месяц: человек + состояние его эссе. */
export interface EssaySummaryRow {
  userId: string;
  name: string;
  position?: string;
  essayId: string | null;
  status: EssayStatus | 'none';
  updatedAt: string | null;
  submittedAt: string | null;
  // Когда вернули на доработку. Отличает «вернули» от «сам не досдал»: статус у
  // обоих один и тот же.
  returnedAt: string | null;
  isEmpty: boolean;
}

// The four essay sections, in display order.
export const ESSAY_SECTIONS = ['done', 'issues', 'plans', 'interesting'] as const;
export type EssaySection = (typeof ESSAY_SECTIONS)[number];

/**
 * Текст ошибки эссе на языке интерфейса: сервер пишет по-русски, поэтому его
 * `message` не показываем, а разбираем код ответа.
 */
export function essayErrorMessage(t: TFunction, error: unknown, fallbackKey: string): string {
  if (!(error instanceof ApiError)) return t(fallbackKey);
  const keys = (error.response.data as { missingSectionKeys?: string[] } | null)
    ?.missingSectionKeys;
  if (error.status === 400 && keys?.length) {
    const sections = keys.map((key) => t(`essays.sections.${key}`)).join(', ');
    return t('essays.errors.missingSections', { sections });
  }
  if (error.status === 403) return t('essays.errors.forbidden');
  if (error.status === 404) return t('essays.errors.notFound');
  if (error.status === 409) return t('essays.errors.exists');
  return t(fallbackKey);
}

export const useMyEssays = (enabled = true) =>
  useQuery({
    queryKey: ['essays', 'mine'],
    queryFn: async (): Promise<EssaySummary[]> => api('/essays/mine'),
    enabled,
  });

export const useEssay = (id?: string) =>
  useQuery({
    queryKey: ['essays', id],
    queryFn: async (): Promise<EssayResponse> => api(`/essays/${id}`),
    enabled: !!id,
  });

export const useCreateEssay = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: { year: number; month: number }): Promise<Essay> =>
      api('/essays', { method: 'POST', body: data }),
    onSuccess: () => {
      trackEvent(AnalyticsEvents.ESSAY_CREATED);
      return qc.invalidateQueries({ queryKey: ['essays', 'mine'] });
    },
  });
};

/**
 * Сохранение содержимого без обвязки react-query — нужно, чтобы дожать
 * отложенный автосейв при размонтировании редактора, когда хук мутации уже мёртв.
 */
export const saveEssayContent = async (
  id: string,
  content: EssayContent,
  // importedAt, который редактор видел при открытии: сервер отобьёт сохранение
  // 409, если импорт с тех пор заменил эссе.
  knownImportedAt?: string | null,
  // updatedAt последней известной версии: сервер отобьёт 409, если эссе
  // с тех пор правил кто-то другой (ассистент, админ).
  knownUpdatedAt?: string,
): Promise<Essay> =>
  api(`/essays/${id}`, {
    method: 'PUT',
    body: {
      ...content,
      ...(knownImportedAt !== undefined && { knownImportedAt }),
      ...(knownUpdatedAt !== undefined && { knownUpdatedAt }),
    },
  });

/** Word-эссе → четыре раздела. Ничего не сохраняет: только разбор файла. */
export const parseEssayDocx = async (file: File): Promise<EssayContent> => {
  const form = new FormData();
  form.append('files', file);
  return apiFormData('/essays/parse-docx', { body: form });
};

export const useSaveEssay = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, content }: { id: string; content: EssayContent }): Promise<Essay> =>
      saveEssayContent(id, content),
    onSuccess: (_, { id }) => {
      qc.invalidateQueries({ queryKey: ['essays', id] });
      qc.invalidateQueries({ queryKey: ['essays', 'mine'] });
    },
  });
};

export const useDeleteEssay = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string): Promise<void> => api(`/essays/${id}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['essays'] }),
  });
};

// Смена статуса эссе. Роуты идемпотентны, поэтому повторный клик безопасен.
const useEssayStatusMutation = (action: 'submit' | 'unsubmit') => {
  const qc = useQueryClient();
  const { t } = useTranslation();
  return useMutation({
    mutationFn: async (id: string): Promise<{ essay: Essay }> =>
      api(`/essays/${id}/${action}`, { method: 'POST' }),
    onSuccess: () => {
      trackEvent(
        action === 'submit' ? AnalyticsEvents.ESSAY_SUBMITTED : AnalyticsEvents.ESSAY_UNSUBMITTED,
      );
      return qc.invalidateQueries({ queryKey: ['essays'] });
    },
    // Сервер отказывает по делу — например, не заполнены обязательные разделы.
    // Без тоста кнопка «Сдать» выглядела бы просто сломанной.
    onError: (error) =>
      showToast({
        title: t('essays.statusFailed'),
        message: essayErrorMessage(t, error, 'common.unknownError'),
        color: 'red',
      }),
  });
};

export const useSubmitEssay = () => useEssayStatusMutation('submit');
export const useUnsubmitEssay = () => useEssayStatusMutation('unsubmit');

/** Возврат автору на доработку: обязательный комментарий уходит ему письмом. */
export const useReturnEssay = () => {
  const qc = useQueryClient();
  const { t } = useTranslation();
  return useMutation({
    mutationFn: async ({
      id,
      comment,
    }: {
      id: string;
      comment: string;
    }): Promise<{ essay: Essay }> =>
      api(`/essays/${id}/return`, { method: 'POST', body: { comment } }),
    onSuccess: () => {
      trackEvent(AnalyticsEvents.DOCUMENT_RETURNED_FOR_REWORK, { kind: 'essay' });
      return qc.invalidateQueries({ queryKey: ['essays'] });
    },
    onError: (error) =>
      showToast({
        title: t('common.error'),
        // 409 — эссе уже не сдано: его вернул кто-то другой (или двойной клик).
        message:
          error instanceof ApiError && error.status === 409
            ? t('essays.errors.alreadyReturned')
            : getErrorMessage(error, t('essays.saveError')),
        color: 'red',
      }),
  });
};

export const useEssaysSummary = (year: number, month: number, enabled = true) =>
  useQuery({
    queryKey: ['essays', 'manage', 'summary', year, month],
    queryFn: async (): Promise<EssaySummaryRow[]> =>
      api(`/essays/manage/summary?year=${year}&month=${month}`),
    enabled,
  });

export function filenameFromContentDisposition(header: string | null): string | null {
  if (!header) return null;
  const encoded = header.match(/filename\*=UTF-8''([^;]+)/i)?.[1];
  if (encoded) return decodeURIComponent(encoded);
  const plain = header.match(/filename="?([^";]+)"?/i)?.[1];
  return plain || null;
}

export async function downloadEssayDocx(essayId: string) {
  const response = await fetch(`${API_URL}/essays/${essayId}/export.docx`, {
    headers: getAuthHeaders(),
  });
  if (!response.ok) throw new Error('Essay export failed');
  const blob = await response.blob();
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download =
    filenameFromContentDisposition(response.headers.get('content-disposition')) || 'essay.docx';
  a.click();
  URL.revokeObjectURL(a.href);
}

export type CompileFormat = 'pdf' | 'docx';

export interface CompileEssaysParams {
  year: number;
  month: number;
  userIds: string[];
  format: CompileFormat;
}

export async function downloadCompiledEssays({ format, ...params }: CompileEssaysParams) {
  const response = await fetch(`${API_URL}/essays/compile${format === 'docx' ? '.docx' : ''}`, {
    method: 'POST',
    headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
    body: JSON.stringify(params),
  });
  if (!response.ok) {
    // Тело несёт причину («нет доступа», кого не хватает) — без него пользователь
    // видит только «не удалось» и не знает, что чинить.
    const body = await response.json().catch(() => null);
    throw new ApiError(body?.message || 'Essay compile failed', body, response.status);
  }
  const blob = await response.blob();
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download =
    filenameFromContentDisposition(response.headers.get('content-disposition')) ||
    `essays.${format}`;
  a.click();
  URL.revokeObjectURL(a.href);
}

// Сборка сводного отчёта: скачивание + тост при ошибке. Причину берём из ответа
// сервера: «нет доступа», «у части выбранных нет эссе» и т.п. — иначе сборщик
// не понимает, кого из выбранных убрать.
export function useCompileEssays() {
  const { t } = useTranslation();
  const mutation = useMutation({
    mutationFn: (params: CompileEssaysParams) => downloadCompiledEssays(params),
    onSuccess: (_, { format }) => trackEvent(AnalyticsEvents.ESSAYS_COMPILED, { format }),
    onError: (error) => {
      const missing =
        error instanceof ApiError
          ? (error.response.data as { missingNames?: string[] } | null)?.missingNames
          : undefined;
      showToast({
        title: t('essays.compileFailed'),
        message: missing?.length
          ? t('essays.compileMissingEssays', { names: missing.join(', ') })
          : getErrorMessage(error, t('essays.compileFailedMessage')),
        color: 'red',
      });
    },
  });
  // Формат текущей сборки — чтобы спиннер крутился на нажатой кнопке, а не на обеих.
  return {
    compile: mutation.mutate,
    compilingFormat: mutation.isPending ? mutation.variables?.format : undefined,
  };
}

// Shared DOCX export: triggers the download and toasts on failure. Used by both
// the My Essays and Manage pages.
export function useExportEssayDocx() {
  const { t } = useTranslation();
  const mutation = useMutation({
    mutationFn: (essayId: string) => downloadEssayDocx(essayId),
    onSuccess: () => trackEvent(AnalyticsEvents.ESSAY_EXPORTED, { format: 'docx' }),
    onError: () =>
      showToast({
        title: t('essays.exportFailed'),
        message: t('essays.exportFailedMessage'),
        color: 'red',
      }),
  });
  return { exportEssay: mutation.mutate, isExporting: mutation.isPending };
}
